import { createElement } from "react";
import {
  createRoute,
  lazyRouteComponent,
  type AnyRoute,
} from "@tanstack/react-router";

import type { FeatureRouteContext } from "@nasam/kernel";
import {
  GEOFENCE_CREATE,
  GEOFENCE_UPDATE,
  GEOFENCE_VIEW,
} from "../../../../permissions";
import { requirePermission } from "../../../../presentation/routes/requirePermission";
import type { GeofencesRepository } from "../../domain";

const GeofencesListScreen = lazyRouteComponent(
  () => import("../GeofencesListScreen"),
  "GeofencesListScreen",
);
const GeofenceCreateScreen = lazyRouteComponent(
  () => import("../GeofenceCreateScreen"),
  "GeofenceCreateScreen",
);
const GeofenceDetailScreen = lazyRouteComponent(
  () => import("../GeofenceDetailScreen"),
  "GeofenceDetailScreen",
);
const GeofenceEditScreen = lazyRouteComponent(
  () => import("../GeofenceEditScreen"),
  "GeofenceEditScreen",
);

export interface GeofencesRouteDeps {
  readonly context: FeatureRouteContext;
  /** e.g. `/fleet/geofences`. */
  readonly basePath: string;
  readonly geofences: GeofencesRepository;
}

export function createGeofencesRoutes({
  context,
  basePath,
  geofences,
}: GeofencesRouteDeps): AnyRoute[] {
  // '$geofenceId' after the literal 'create', so it's never taken for an id.
  const detailRoute = createRoute({
    getParentRoute: context.getParentRoute,
    path: `${basePath}/$geofenceId`,
    beforeLoad: requirePermission([GEOFENCE_VIEW]),
    component: function GeofenceDetailRouteScreen() {
      // Typed by hand: the route refers to itself here (same convention as
      // the POI routes).
      const { geofenceId } = detailRoute.useParams() as unknown as {
        geofenceId: string;
      };
      return createElement(GeofenceDetailScreen, {
        repository: geofences,
        basePath,
        geofenceId,
      });
    },
  });

  const editRoute = createRoute({
    getParentRoute: context.getParentRoute,
    path: `${basePath}/$geofenceId/edit`,
    beforeLoad: requirePermission([GEOFENCE_UPDATE]),
    component: function GeofenceEditRouteScreen() {
      // Typed by hand: the route refers to itself here (same convention as
      // the POI routes).
      const { geofenceId } = editRoute.useParams() as unknown as {
        geofenceId: string;
      };
      return createElement(GeofenceEditScreen, {
        repository: geofences,
        basePath,
        geofenceId,
      });
    },
  });

  return [
    createRoute({
      getParentRoute: context.getParentRoute,
      path: basePath,
      beforeLoad: requirePermission([GEOFENCE_VIEW]),
      component: () =>
        createElement(GeofencesListScreen, { repository: geofences, basePath }),
    }),
    createRoute({
      getParentRoute: context.getParentRoute,
      path: `${basePath}/create`,
      beforeLoad: requirePermission([GEOFENCE_CREATE]),
      component: () =>
        createElement(GeofenceCreateScreen, { repository: geofences, basePath }),
    }),
    detailRoute,
    editRoute,
  ];
}
